// external imports
import React, { useRef, useState, useEffect } from 'react';
import {
	Button,
	Col,
	CustomInput,
	FormFeedback,
	Form,
	FormGroup,
	Input,
	Label,
	Modal,
	ModalBody,
	ModalFooter,
	ModalHeader,
	Row,
	Table
} from 'reactstrap';
import { Formik } from 'formik';
import { useSelector, useDispatch } from 'react-redux';

// internal imports
import {
	openInGeojsonIO,
	copyAOI,
	downloadGeojson,
	downloadKML
} from 'screens/Sites/Helpers';
import { renderTileLayerMenu } from 'screens/Sites/map/TileLayerMenu';
import { AppMap } from 'screens/Sites/Map';
import { getDrawnAreaDetails } from 'screens/Sites/Helpers';
import { INITIAL_DRAWING, INITIAL_FORM_VALUES } from 'screens/Sites/Sites';
import { getAOILayer } from 'screens/Sites/map/getAOILayer';
import { renderSubsitesInfo } from 'screens/Sites/map/SubsiteInfo';
import { commodityColorExpression } from 'screens/Sites/Helpers';
import { getMapSiteLayers2 } from 'screens/Sites/Helpers';
import { renderCommodityLegend } from 'screens/Sites/map/CommodityLegend';
import { readUploadedFile } from 'screens/Sites/map/fileUpload';
import { createSite, updateSiteAction } from 'actions';
import { FormCapacityTable } from 'components/CapacityTable';
import { CommentsTable } from 'components/CommentsTable';
import { renderAdditionalMapBtns } from 'screens/Sites/map/additionalMapBtns';
import { downloadKml } from 'actions';

// Map imports - don't remove these or map won't appear
import ReactMapboxGl from 'react-mapbox-gl';
import DrawControl from 'react-mapbox-gl-draw';
import mapboxGlDraw from 'mapbox-gl-draw';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/src/css/mapbox-gl.css';

mapboxgl.workerClass =
	require('worker-loader!mapbox-gl/dist/mapbox-gl-csp-worker').default; // eslint-disable-line

const CreateAndUpdateSiteModal = ({
	isOpen,
	toggleCreate,
	drawing,
	setDrawing,
	formValue,
	setFormValue,
	parentSite,
	listMapProps,
	filteredSites,
	siteFilters,
	setSiteFilters,
	updateSite,
	setUpdateSite
}) => {
	const formikRef = useRef();
	const mapRef = useRef(null);
	const popupRef = useRef(null);

	const dispatch = useDispatch();

	const [mouseOverId, setMouseOverId] = useState(null);
	const [uploadedFile, setUploadedFile] = useState(null);
	const [initialDrawFeature, setInitialDrawFeature] = useState(null);

	const commodities = useSelector((state) => state.commodities);
	const sites = useSelector((state) => state.sites);
	const regions = useSelector((state) => state.regions);
	const countries = useSelector((state) => state.countries);
	const siteTypes = useSelector((state) => state.siteTypes);

	const [capacitiesCollapseIsOpen, setCapacitiesCollapseIsOpen] =
		useState(true);
	const [commentsCollapseIsOpen, setCommentsCollapseIsOpen] = useState(false);

	// console.log('updateSite', updateSite);
	// console.log('parentSite', parentSite);
	// console.log('drawing', drawing);

	const mapboxJSONs = getMapSiteLayers2(
		filteredSites.filter((s) => s.id !== updateSite?.id),
		siteTypes
	);
	const allSitesLayer = !sites?.length
		? []
		: [
				{
					id: 'smelters',
					type: 'circle',
					data: mapboxJSONs.smelter,
					paint: {
						'circle-color': commodityColorExpression,
						'circle-radius': 5
					}
				},
				{
					id: 'stockpiles',
					type: 'circle',
					data: mapboxJSONs.stockpiles,
					paint: {
						'circle-color': commodityColorExpression,
						'circle-radius': 5
					}
				},
				{
					id: 'airfields',
					type: 'circle',
					data: mapboxJSONs.airfield,
					paint: {
						'circle-color': commodityColorExpression,
						'circle-radius': 5
					}
				}
			];

	// const initialDrawFeature =
	// 	updateSite?.id && drawing?.drawAOI
	// 		? {
	// 				id: `site-${updateSite.id}`,
	// 				type: 'Feature',
	// 				geometry: drawing.drawAOI,
	// 				properties: {
	// 					id: updateSite.id,
	// 					name: updateSite.name
	// 				}
	// 			}
	// 		: null;

	useEffect(() => {
		if (!updateSite?.id || !drawing?.drawAOI) {
			setInitialDrawFeature(null);
		} else {
			setInitialDrawFeature({
				id: `site-${updateSite.id}`,
				type: 'Feature',
				geometry: drawing.drawAOI,
				properties: {
					id: updateSite.id,
					name: updateSite.name
				}
			});
		}
	}, [updateSite, drawing]);

	useEffect(() => {
		const mapDeleteBtn = document.getElementsByClassName(
			'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_trash'
		)[0];
		const mapDrawBtn = document.getElementsByClassName(
			'mapbox-gl-draw_ctrl-draw-btn mapbox-gl-draw_polygon'
		)[0];

		if (!mapDeleteBtn || !mapDrawBtn) return;

		mapDeleteBtn.disabled = uploadedFile;
		mapDrawBtn.disabled = uploadedFile || drawing.drawAOI;
	}, [uploadedFile, drawing]);

	const parseDrawnGeometry = async (feature) => {
		console.log('parseDrawnGeometry', feature);
		if (!feature) return;

		const areaInfo = await getDrawnAreaDetails(
			feature.geometry,
			parentSite?.site,
			parentSite?.aoi,
			countries,
			regions
		);

		if (areaInfo?.error) {
			setDrawing((prev) => ({
				...prev,
				drawError: areaInfo?.error
			}));
		} else if (areaInfo?.country && areaInfo?.region) {
			setDrawing((prev) => ({
				...prev,
				drawAOI: feature.geometry,
				drawArea: areaInfo.area,
				drawLat: areaInfo.center[1],
				drawLong: areaInfo.center[0],
				drawCountry: areaInfo?.country && areaInfo.country,
				drawRegion: areaInfo?.region && areaInfo.region
			}));
		} else {
			setDrawing((prev) => ({
				...prev,
				drawError:
					'This country does not exist in the database.  Please delete the AOI and try another country.'
			}));
		}

		return areaInfo;
	};

	const handleDrawCreate = async (features) => {
		console.log('draw feature', features);
		if (!features[0]) return;
		await parseDrawnGeometry(features[0]);
	};

	const handleDrawDelete = (_features) => {
		setDrawing(INITIAL_DRAWING);
	};

	const handleDrawUpdate = async (features) => {
		setDrawing((prev) => ({
			...prev,
			drawError: null
		}));
		if (!features[0]) return;
		await parseDrawnGeometry(features[0]);
	};

	const handleFileUpload = async (e) => {
		const file = e.target.files?.[0];
		if (!file) return;

		const { valid, geometry: geojson } = await readUploadedFile(
			file,
			setUploadedFile
		);

		if (!valid) {
			throw new Error('Uploaded file not valid');
		}

		console.log('uploaded and parsed geojson', geojson);

		const fc = {
			type: 'Feature',
			properties: {},
			geometry: {
				type: 'Polygon',
				coordinates: geojson?.coordinates
			}
		};

		let info = null;

		if (!geojson) {
			console.error('invalid');
		} else {
			info = await parseDrawnGeometry(fc);
		}

		if (!info) return;

		handleZoomToSite(info.center[1], info.center[0], 12);

		const feature = geojson;
		mapRef.current.addDrawFeature(fc);
	};

	const handleFileDelete = () => {
		setUploadedFile(null);
		setDrawing(INITIAL_DRAWING);
		mapRef.current.deleteAllDrawFeatures();
	};

	const handleMouseOverLayer = (feature, e, map) => {
		if (!feature) return;

		if (popupRef.current) {
			popupRef.current.remove();
		}

		setMouseOverId(feature?.properties?.id);

		popupRef.current = new mapboxgl.Popup({
			closeOnClick: false,
			closeButton: false
		});

		const { id, name } = feature.properties;

		let siteIdString = '';
		let nameString = '';

		if (id === 987123) {
			siteIdString = `New site ID`;
			nameString = `New site name`;
		} else {
			siteIdString = `Site ${id}`;
			nameString = `${name}`;
		}

		map.getCanvas().style.cursor = 'pointer';

		popupRef.current
			.setLngLat([e.lngLat.lng, e.lngLat.lat])
			.setHTML(
				`
			   <div class="map-popup">
					<div class="map-popup__id">${siteIdString}</div>
					<div class="map-popup__name">${nameString}</div>
				</div>
			`
			)
			.addTo(map);
	};

	const handleMouseLeaveLayer = (_feature, _e, map, _layer) => {
		setMouseOverId(null);

		map.getCanvas().style.cursor = '';

		if (popupRef.current) {
			popupRef.current.remove();
			popupRef.current = null;
		}
	};

	const onCreateAction = async ({ values, setSubmitting }) => {
		// const site_type_id = siteTypes.find((st) =>
		// 	updateSite ? st.name === values.stype : st.name === values.site_type_id
		// );

		let submitJson = {
			id: values.id,
			name: values.name,
			aoi: drawing.drawAOI,
			params: {
				twin_id: parseInt(values?.params?.twin_id) || null,
				capacity: parseInt(values?.params?.capacity) || 0,
				end_date: values?.params?.end_date || null,
				use_smoke: parseInt(values?.params?.use_smoke) || 0,
				prod_state:
					values?.params?.prod_state == null
						? null
						: Number(values?.params?.prod_state),
				start_date: values?.params?.start_date || null,
				plant_type: values?.params?.plant_type || null
			},
			site_type_id: values?.site_type_id,
			commodity_id: parentSite ? null : values.commodity_id, // subsite commodity_id is null
			country_id: drawing.drawCountry,
			company: values.company
		};

		if (parentSite) {
			Object.assign(submitJson, {
				parent_id: values.parent_id,
				parent_type_id: values.parent_type_id
			});
		}

		console.log('submitJson', submitJson);

		if (updateSite) {
			dispatch(updateSiteAction({ id: updateSite.id, body: submitJson }));
		} else {
			dispatch(createSite(submitJson));
		}

		setSubmitting(false);
		setDrawing(INITIAL_DRAWING);
		setFormValue(INITIAL_FORM_VALUES);

		setTimeout(() => {
			toggleCreate();
		}, 500);
	};

	const handleZoomToSite = (lat, long, zoom) => {
		//console.log('Zoom to lat/long', lat, long);
		mapRef.current.recenter([long, lat], zoom);
	};

	const handleChangeSiteType = (setFieldValue, value) => {
		const siteTypeId = value ? Number(value) : null;

		const siteType = siteTypes.find(({ id }) => id === siteTypeId);
		const params =
			siteType?.template?.reduce((acc, { name, type }) => {
				acc[name] = type === 'String' ? '' : null;
				return acc;
			}, {}) ?? {};

		setFieldValue('site_type_id', siteTypeId);
		setFieldValue('stype', siteType?.name ?? null);
		setFieldValue('commodity_id', null);
		setFieldValue('params', params);
	};

	const handleUndoAOIChanges = () => {
		if (!drawing?.drawAOI || !mapRef.current) return;

		const originalFeature = {
			id: `site-${updateSite.id}`,
			type: 'Feature',
			geometry: {
				type: 'Polygon',
				coordinates: drawing.endpointAOI.aoi.coordinates[0]
			},
			properties: {
				id: updateSite.id,
				name: updateSite.name
			}
		};

		mapRef.current.resetDrawFeature(originalFeature);

		setDrawing((prev) => ({
			...prev,
			drawAOI: {
				type: 'Polygon',
				coordinates: drawing.endpointAOI.aoi.coordinates[0]
			},
			drawLat: updateSite.lat,
			drawLong: updateSite.lon,
			//drawArea: area(polygon(thisSiteAOI.aoi.coordinates[0])) / 1000000,
			drawCountry: countries.find((c) => c.name === updateSite.country)?.id,
			drawRegion: regions.find((r) => r.name === updateSite.region)?.id
		}));

		handleZoomToSite(updateSite.lat, updateSite.lon, 13);
	};

	const renderMapRow = () => {
		const layers = [...allSitesLayer];

		if (parentSite) {
			const parentLayer = getAOILayer(parentSite.aoi, parentSite?.site, {
				fillColor: 'red',
				fillOpacity: 0.1
			});

			const subsiteLayers = parentSite?.subsiteAOIs?.map((ss) =>
				getAOILayer(ss.aoi, ss, { fillColor: 'green', fillOpacity: 0.1 })
			);

			layers.push(parentLayer);
			layers.push(...subsiteLayers);
		}

		const renderHotspotsKMLButton = () => {
			return (
				<Button
					id="downloadHotspotsKMLButton"
					size="sm"
					type="button"
					onClick={() => {
						const comm = commodities.find(
							(comm) => comm.id === updateSite.commodity_id
						)?.symbol;
						if (!comm) return;
						downloadKml({ commodity: comm, id: updateSite.id });

						return true;
					}}
				>
					Hotspot KML download <i className="ms-2 fa-solid fa-download"></i>
				</Button>
			);
		};

		return (
			<Row>
				<Col lg={12}>
					<div>
						<div>
							<label
								id="uploadFileButton"
								className="custom-file-upload"
								title={
									drawing.drawAOI
										? 'Delete the drawn AOI before uploading a file'
										: 'Upload a valid .kml or .geojson file'
								}
							>
								<input
									type="file"
									id="fileUpload"
									onChange={(e) => !drawing.drawAOI && handleFileUpload(e)}
									name="fileUpload"
									disabled={drawing.drawAOI}
								/>
								<i className="fa-solid fa-file-arrow-up"></i>
							</label>
						</div>

						{renderAdditionalMapBtns(
							handleZoomToSite,
							handleUndoAOIChanges,
							drawing,
							updateSite
						)}

						{renderHotspotsKMLButton()}

						{renderTileLayerMenu(drawing, setDrawing)}

						{renderCommodityLegend(commodities, siteFilters, setSiteFilters)}

						{parentSite && <>{renderSubsitesInfo(parentSite, mouseOverId)}</>}

						<AppMap
							ref={mapRef}
							tileLayer={drawing.tileLayer}
							center={[listMapProps.long, listMapProps.lat]}
							zoom={listMapProps.zoom}
							layers={layers}
							draw={true}
							initialDrawFeature={initialDrawFeature}
							onDrawCreate={handleDrawCreate}
							onDrawDelete={handleDrawDelete}
							onDrawUpdate={handleDrawUpdate}
							onFeatureMouseEnter={handleMouseOverLayer}
							onFeatureMouseLeave={handleMouseLeaveLayer}
						/>
					</div>
				</Col>
			</Row>
		);
	};

	const renderAOISection = () => {
		return (
			<>
				<Label className="form-section-names px-5 pt-2">Area of Interest</Label>
				<Row className="d-flex justify-content-center px-5">
					<Col md={3} className="d-flex flex-column justify-content-center">
						{uploadedFile?.file?.name && (
							<Row title="Uploaded file">
								<Col md={2}>
									<button
										id="deleteUploadFile"
										onClick={handleFileDelete}
										title="Delete uploaded file"
									>
										<i className="fa-solid fa-trash-can"></i>
									</button>
								</Col>
								<Col md={10} className="text-truncate">
									<p>{uploadedFile?.file?.name}</p>
								</Col>
							</Row>
						)}

						{uploadedFile?.error && (
							<p className="text-center text-danger">{uploadedFile.error}</p>
						)}
					</Col>

					<Col md={5}>
						<div>
							<textarea
								id="createSiteAOITextArea"
								className="form-control-sm form-control"
								name="textValue"
								value={
									drawing.drawAOI && !drawing.drawError
										? JSON.stringify(drawing.drawAOI, undefined, 4)
										: ''
								}
								rows={7}
								cols={42}
								placeholder={
									!drawing.drawAOI && !drawing.drawError
										? 'Draw a polygon on the map or upload a file to create an AOI'
										: ''
								}
								readOnly
							/>
						</div>

						<div>
							<Row className="d-flex justify-content-center px-4">
								<Col className="px-1 py-1">
									<Button
										color="light"
										size="sm"
										className="AOIInfoButtons"
										id="createSiteOpenInGeojson"
										type="button"
										onClick={
											drawing.drawArea
												? () => openInGeojsonIO(drawing.drawAOI)
												: undefined
										}
										title="Open in geojson.io"
										disabled={!drawing.drawArea}
										cursor="pointer"
									>
										<i className="fa-solid fa-up-right-from-square mr-2" />
									</Button>
								</Col>
								<Col className="px-1 py-1">
									<Button
										color="light"
										size="sm"
										className="AOIInfoButtons"
										type="button"
										id="copyAOI"
										title="Copy AOI to clipboard"
										disabled={!drawing.drawArea}
										onClick={
											drawing.drawArea
												? () => copyAOI(drawing.drawAOI)
												: undefined
										}
										cursor="pointer"
									>
										<i className="fa-solid fa-copy mr-2" />
									</Button>
								</Col>
								<Col className="px-1 py-1">
									<Button
										color="light"
										size="sm"
										className="AOIInfoButtons"
										type="button"
										id="downloadKML"
										onClick={
											drawing.drawArea
												? () =>
														downloadKML({
															geometry: drawing.drawAOI,
															site: updateSite ? updateSite : null
														})
												: undefined
										}
										disabled={!drawing.drawArea}
										title="Download .kml file"
										cursor="pointer"
									>
										<i className="fa-solid fa-download mr-2" />
										kml
									</Button>
								</Col>

								<Col className="px-1 py-1">
									<Button
										color="light"
										className="AOIInfoButtons"
										size="sm"
										type="button"
										id="downloadgeoJSON"
										onClick={
											drawing.drawArea
												? () =>
														downloadGeojson({
															geometry: drawing.drawAOI,
															site: updateSite ? updateSite : null
														})
												: undefined
										}
										disabled={!drawing.drawArea}
										title="Download .geojson file"
									>
										<i className="fa-solid fa-download mr-2" />
										json
									</Button>
								</Col>
							</Row>
						</div>

						{drawing.drawError && (
							<div>
								<p className="text-danger">
									AOI not valid: {drawing.drawError}
								</p>
							</div>
						)}
					</Col>

					<Col md={4}>
						<Table id="aoi-table">
							<tbody>
								<tr>
									<td>
										<Label>
											Area km<sup>2</sup>
										</Label>
									</td>
									<td>
										<Input
											type="text"
											disabled={true}
											bsSize="sm"
											value={drawing?.drawArea?.toFixed(2) || ''}
										></Input>
									</td>
								</tr>

								<tr>
									<td>
										<Label>Latitude</Label>
									</td>
									<td>
										<Input
											type="text"
											disabled={true}
											bsSize="sm"
											value={drawing?.drawLat?.toFixed(2) || ''}
										></Input>
									</td>
								</tr>

								<tr>
									<td>
										<Label>Longitude</Label>
									</td>
									<td>
										<Input
											type="text"
											disabled={true}
											bsSize="sm"
											value={drawing?.drawLong?.toFixed(2) || ''}
										></Input>
									</td>
								</tr>

								<tr>
									<td>
										<Label>Country</Label>
									</td>
									<td>
										<Input
											id="createSiteCountry"
											type="select"
											disabled={true}
											bsSize="sm"
											value={drawing.drawCountry || undefined}
										>
											<option value="">Select a country</option>
											{countries.map(({ id, name }, idx) => (
												<option value={id} key={`country${idx}`}>
													{name}
												</option>
											))}
										</Input>
									</td>
								</tr>

								<tr>
									<td>
										<Label>Region</Label>
									</td>
									<td>
										<Input
											id="createSiteRegion"
											type="select"
											disabled={true}
											bsSize="sm"
											value={drawing.drawRegion || undefined}
										>
											<option value="">Select a region</option>
											{regions.map(({ id, name }, idx) => (
												<option value={id} key={`region${idx}`}>
													{name}
												</option>
											))}
										</Input>
									</td>
								</tr>
							</tbody>
						</Table>
					</Col>
				</Row>
			</>
		);
	};

	const renderFormikForm = () => {
		if (!formValue) return;

		const getBtnString = () => {
			if (updateSite) {
				return `Update site`;
			}

			if (parentSite) {
				return `Create subsite`;
			}

			return 'Create site';
		};

		return (
			<Formik
				innerRef={formikRef}
				initialValues={formValue}
				validate={(values) => {
					const errors = {};

					const site_ids = sites.map((site) => site.id);
					const ID = parseInt(values.id);

					if (site_ids.includes(ID) && !updateSite) {
						errors.id = 'This site ID already exists';
					} else if (values.id.length === 0) {
						errors.id = 'Provide a site ID';
					} else if (ID > 999999 || ID < 0) {
						errors.id = 'Site ID out of range (0 - 999999)';
					}

					if (values?.name?.trim().length === 0) {
						errors.name = 'A name is required';
					} else if (values?.name?.trim().length > 80) {
						errors.name = 'Name should be no more than 80 characters';
					}

					if (values?.company?.trim().length === 0) {
						errors.company = 'Company details are required';
					} else if (values?.company?.trim().length > 256) {
						errors.company =
							'Company details should be no more than 256 characters';
					}

					if (!values?.site_type_id) {
						errors.site_type_id = 'A site type is required';
					}

					const siteTypesWithCommodities = [
						...new Set(
							commodities
								.map(
									(commodity) =>
										siteTypes.find(
											(siteType) => siteType.name === commodity.site_type
										)?.id
								)
								.filter(Boolean)
						)
					];

					const requiresCommodity = siteTypesWithCommodities.includes(
						values.site_type_id
					);

					const availableCommoditiesForChosenSiteType = commodities
						.filter(
							(c) =>
								c.site_type ===
								siteTypes.find((ss) => ss.id === parseInt(values.site_type_id))
									?.name
						)
						?.map((c) => c.id);

					if (requiresCommodity && !values.commodity_id) {
						errors.commodity_id = 'A commodity is required';
					} else if (
						requiresCommodity &&
						!availableCommoditiesForChosenSiteType.includes(values.commodity_id)
					) {
						errors.commodity_id =
							'This commodity ID cannot be chosen with this site type';
					}

					if (values.site_type_id === 1) {
						const validProdStates = [-2, -1, 0, 1, 2, 5];
						const prodState = values?.params?.prod_state;

						if (prodState == null) {
							errors.prod_state = `A production state is required and must be one of: ${validProdStates.join(', ')}`;
						} else if (!validProdStates.includes(prodState)) {
							errors.prod_state = `Production state must be one of: ${validProdStates.join(', ')}`;
						}
					}

					if (drawing.drawError) {
						errors.aoi = 'AOI error';
						document
							.getElementById('createSiteAOITextArea')
							.classList.add('is-invalid');
					} else {
						delete errors.aoi;
					}

					console.log('errors', errors);
					return errors;
				}}
				onSubmit={(values, { setSubmitting }) => {
					setUploadedFile(null);
					onCreateAction({ values, setSubmitting });
				}}
			>
				{({
					values,
					errors,
					touched,
					handleChange,
					handleBlur,
					setFieldValue,
					handleSubmit,
					isSubmitting
				}) => {
					console.log('Values:', values);

					const selectedSiteType = siteTypes.find(
						({ id }) => id === Number(values.site_type_id)
					)?.name;

					return (
						<Form onSubmit={handleSubmit}>
							<ModalBody>
								<Label className="form-section-names pt-1">
									Site Information
								</Label>

								<Row>
									<Col md={2}>
										<FormGroup>
											<Label for="createSiteId">Site ID</Label>
											<Input
												name="id"
												value={values.id}
												onChange={handleChange}
												onBlur={handleBlur}
												type="number"
												id="createSiteId"
												placeholder="14507"
												invalid={Boolean(errors.id && touched.id)}
												bsSize="sm"
												disabled={updateSite}
											/>
											<FormFeedback>{errors.id}</FormFeedback>
										</FormGroup>
									</Col>

									<Col md={2}>
										<FormGroup>
											<Label for="createParentSiteId">Parent Site ID</Label>
											<Input
												type="text"
												id="createParentSiteId"
												name={values?.parent_id}
												value={values?.parent_id || ''}
												placeholder="null"
												bsSize="sm"
												title={
													!parentSite
														? 'This is a parent site, therefore Parent Site Id is null'
														: `Parent Site: ${parentSite.site.id} - ${parentSite.site.name}`
												}
												disabled={true}
											/>
										</FormGroup>
									</Col>

									<Col md={4}>
										<FormGroup>
											<Label for="createSiteName">Site name</Label>
											<Input
												id="createSiteName"
												name="name"
												placeholder="Kalgoorlie"
												value={values.name}
												onChange={handleChange}
												onBlur={handleBlur}
												invalid={Boolean(errors.name && touched.name)}
												bsSize="sm"
												type="search"
											/>
											<FormFeedback>{errors.name}</FormFeedback>
										</FormGroup>
									</Col>
									<Col md={4}>
										<FormGroup>
											<Label for="createSiteCompany">Company</Label>
											<Input
												id="createSiteCompany"
												placeholder="BHP Billiton Nickel West"
												value={values.company}
												name="company"
												onChange={handleChange}
												onBlur={handleBlur}
												invalid={Boolean(errors.company && touched.company)}
												bsSize="sm"
												type="search"
											/>
											<FormFeedback>{errors.company}</FormFeedback>
										</FormGroup>
									</Col>
								</Row>

								<FormGroup>
									<Row form>
										<Col md={3}>
											<FormGroup>
												<Label for="createSiteType">Site Type</Label>
												<Input
													id="createSiteType"
													type="select"
													name="site_type_id"
													onChange={(e) =>
														handleChangeSiteType(setFieldValue, e.target.value)
													}
													onBlur={handleBlur}
													value={values.site_type_id || ''}
													invalid={Boolean(
														errors.site_type_id && touched.site_type_id
													)}
													bsSize="sm"
													disabled={updateSite}
												>
													<option value="">Please select a site type</option>
													{siteTypes.map(({ id, name, template }, idx) => (
														<option value={id} key={`sitetype${idx}`}>
															{name}
														</option>
													))}
												</Input>
												<FormFeedback>{errors.site_type_id}</FormFeedback>
											</FormGroup>
										</Col>

										<Col md={3}>
											<FormGroup>
												<Label for="createSiteCommodity">Commodity</Label>
												<CustomInput
													id="createSiteCommodity"
													name="commodity_id"
													type="select"
													onChange={(e) => {
														setFieldValue(
															'commodity_id',
															e.target.value ? Number(e.target.value) : null
														);
													}}
													onBlur={handleBlur}
													placeholder="Please select a commodity type"
													value={parseInt(values.commodity_id) || ''}
													invalid={Boolean(
														errors.commodity_id && touched.commodity_id
													)}
													bsSize="sm"
													disabled={updateSite}
												>
													<option value={null}>Select a commodity type</option>
													{commodities
														.filter(
															({ site_type }) => site_type === selectedSiteType
														)
														.map(({ id, symbol, site_type }, idx) => (
															<option value={parseInt(id)} key={`comm${idx}`}>
																{symbol}
															</option>
														))}
												</CustomInput>
												<FormFeedback>{errors.commodity_id}</FormFeedback>
											</FormGroup>
										</Col>
									</Row>
								</FormGroup>

								<Row>
									<FormGroup>
										<Col md={12}>
											{siteTypes
												.filter(
													({ id }) => id === parseInt(values.site_type_id)
												)
												.map((item) => {
													return (
														<Table
															id="createSiteParamsTable"
															key={`key${item.name}`}
														>
															<tbody>
																{item.template.map(
																	({ name, type, options }, idx) => {
																		return (
																			<tr key={`key${name}`}>
																				<td>{name}</td>
																				<td>
																					<Input
																						name={`params.${name}`}
																						id={name}
																						value={values.params?.[name] ?? ''}
																						onChange={handleChange}
																						onBlur={handleBlur}
																						bsSize="sm"
																						invalid={errors.hasOwnProperty(
																							name
																						)}
																						type={
																							options
																								? 'select'
																								: type.toLowerCase()
																						}
																						// disabled={
																						// 	name === 'capacity' && updateSite
																						// }
																						title={
																							name === 'capacity'
																							? 'Edit existing capacity records or add new records in the capacity table below.'
																								: undefined
																						}
																					>
																						<option
																							key={idx - 1}
																							value={undefined}
																						>
																							{''}
																						</option>
																						{options?.map(({ value }, idx) => (
																							<option key={idx} value={value}>
																								{value}
																							</option>
																						))}
																					</Input>
																					<FormFeedback>{`${errors[name]}`}</FormFeedback>
																				</td>
																			</tr>
																		);
																	}
																)}
															</tbody>
														</Table>
													);
												})}
										</Col>
									</FormGroup>
								</Row>

								{updateSite && <>{renderTableAccordions()}</>}
							</ModalBody>

							<ModalFooter>
								<Button
									id="createSiteSubmitButton"
									color="info"
									size="sm"
									type="submit"
									disabled={
										isSubmitting || !drawing.drawAOI || drawing.drawError
									}
								>
									{getBtnString()}
								</Button>{' '}
								<Button
									color="secondary"
									size="sm"
									type="button"
									onClick={() => {
										setUploadedFile(null);
										toggleCreate();
									}}
								>
									Cancel
								</Button>
							</ModalFooter>
						</Form>
					);
				}}
			</Formik>
		);
	};

	const getModalTitle = () => {
		if (updateSite) {
			return `Update site ${updateSite.name}`;
		}

		if (parentSite) {
			return `Create a new subsite for ${parentSite.site.id} - ${parentSite.site.name}`;
		}

		return 'Create a new site';
	};

	const renderTableAccordions = () => {
		return (
			<Row className="d-flex justify-contents-center px-4 mt-2">
				<div id="accordion" style={{ width: '100%' }}>
					<div className="card">
						<div
							className="card-header"
							id="headingOne"
							data-toggle="collapse"
							data-target="#collapseOne"
							aria-expanded={capacitiesCollapseIsOpen}
							aria-controls="collapseOne"
							onClick={(e) => {
								e.preventDefault();
								setCapacitiesCollapseIsOpen((prev) => !prev);
							}}
						>
							<h6 className="mb-0">
								Historic Capacities{' '}
								<span className="text-end">
									{capacitiesCollapseIsOpen ? (
										<i className="fa-solid fa-chevron-up text-end"></i>
									) : (
										<i className="fa-solid fa-chevron-down text-end"></i>
									)}
								</span>
							</h6>
						</div>
						<div
							id="collapseOne"
							className="collapse show"
							aria-labelledby="headingOne"
							data-parent="#accordion"
						>
							<FormCapacityTable site={updateSite} />
						</div>
					</div>

					<div className="card">
						<div
							className="card-header"
							id="headingTwo"
							data-toggle="collapse"
							data-target="#collapseTwo"
							aria-expanded={commentsCollapseIsOpen}
							aria-controls="collapseTwo"
							onClick={(e) => {
								e.preventDefault();
								setCommentsCollapseIsOpen((prev) => !prev);
							}}
						>
							<h6 className="mb-0">
								Site Comments{' '}
								{commentsCollapseIsOpen ? (
									<i className="fa-solid fa-chevron-up text-end"></i>
								) : (
									<i className="fa-solid fa-chevron-down text-end"></i>
								)}
							</h6>
						</div>
						<div
							id="collapseTwo"
							className="collapse"
							aria-labelledby="headingTwo"
							data-parent="#accordion"
						>
							<CommentsTable updateSite={updateSite} />
						</div>
					</div>
				</div>
			</Row>
		);
	};

	return (
		<Modal isOpen={isOpen} toggle={toggleCreate} size="xl" closeonclick="true">
			<ModalHeader toggle={toggleCreate}>
				<i className="fas fa-plus mr-3" />
				{getModalTitle()}
			</ModalHeader>

			{renderMapRow()}

			{renderAOISection()}

			{renderFormikForm()}
		</Modal>
	);
};

export default CreateAndUpdateSiteModal;
